// Evidencias por acción del plan (PE-A4, PO-18). Tabla emergency_plan_action_evidence (M37): varios archivos por
// acción, en emergency-files, carpeta <empresa>/planes/<plan>/acciones/<acción>/. JPG, PNG, WEBP o PDF hasta 10 MB,
// y la cabecera del archivo tiene que coincidir con su tipo (como la hoja de seguridad: un .docx renombrado no pasa).
// Se abren con una URL firmada de 120 s. Manager y admin suben; solo admin borra; viewer solo mira (RLS de M37 y
// políticas ef_* de M33/M36). Una acción obligatoria no se cierra sin evidencia (trigger de M37 y validarAccion).
// emergency_plan_actions.evidence_path (M33) también cuenta como evidencia.

import { supabase } from '../../supabaseClient';
import type { FilaAccion } from './pe-acciones';
import { MENSAJE_CIERRE_SIN_EVIDENCIA } from './pe-acciones';
import { mensajeErrorPE, MENSAJE_SIN_PERMISO } from './pe-datos';
import { BUCKET_PLAN } from './pe-versiones';

export const SEGUNDOS_URL_EVIDENCIA = 120;
export const EVIDENCIA_MAX_MB = 10;
export const EVIDENCIA_MAX_BYTES = EVIDENCIA_MAX_MB * 1024 * 1024;

export type TipoEvidencia = 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf';
export const TIPOS_EVIDENCIA: TipoEvidencia[] = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
export const ACEPTA_EVIDENCIA = '.jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf';

const TIPO_POR_EXTENSION: Record<string, TipoEvidencia> = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', pdf: 'application/pdf' };

export const MENSAJE_EVIDENCIA_TIPO = 'La evidencia debe ser una foto (JPG, PNG o WEBP) o un PDF.';
export const MENSAJE_EVIDENCIA_TAMANO = `La evidencia supera el tamaño máximo permitido (${EVIDENCIA_MAX_MB} MB).`;
export const MENSAJE_EVIDENCIA_VACIA = 'El archivo está vacío.';
export const MENSAJE_EVIDENCIA_CABECERA = 'El contenido del archivo no corresponde a su tipo (JPG, PNG, WEBP o PDF).';
export { MENSAJE_CIERRE_SIN_EVIDENCIA };

export interface FilaEvidencia {
  id: string;
  company_id: string;
  plan_id: string;
  action_id: string;
  storage_path: string;
  file_name: string;
  mime_type: TipoEvidencia;
  size_bytes: number;
  uploaded_by: string | null;
  created_at: string;
}

// Tipo que declara el archivo: el del navegador si es uno de los cuatro; si no, el de la extensión.
export function tipoDeclarado(archivo: { name: string; type: string }): TipoEvidencia | null {
  if ((TIPOS_EVIDENCIA as string[]).includes(archivo.type)) return archivo.type as TipoEvidencia;
  const ext = /\.([a-z0-9]+)$/i.exec(archivo.name)?.[1]?.toLowerCase() ?? '';
  return TIPO_POR_EXTENSION[ext] ?? null;
}

// Tipo real según los primeros bytes: JPG FF D8 FF; PNG 89 50 4E 47; WEBP "RIFF"····"WEBP"; PDF "%PDF".
export function tipoPorCabecera(b: Uint8Array): TipoEvidencia | null {
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return 'image/jpeg';
  if (b.length >= 4 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return 'image/png';
  if (b.length >= 12 && b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46
    && b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50) return 'image/webp';
  if (b.length >= 4 && b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46) return 'application/pdf';
  return null;
}

// Primeros 12 bytes con FileReader (existe también en las pruebas).
export function leerCabecera(archivo: Blob): Promise<Uint8Array> {
  return new Promise(resolve => {
    const lector = new FileReader();
    lector.onload = () => resolve(new Uint8Array(lector.result as ArrayBuffer));
    lector.onerror = () => resolve(new Uint8Array(0));
    lector.readAsArrayBuffer(archivo.slice(0, 12));
  });
}

// Validación sin leer el archivo: tipo y tamaño. Mensaje para el usuario o null.
export function validarEvidencia(archivo: { name: string; type: string; size: number }): string | null {
  if (!tipoDeclarado(archivo)) return MENSAJE_EVIDENCIA_TIPO;
  if (archivo.size <= 0) return MENSAJE_EVIDENCIA_VACIA;
  if (archivo.size > EVIDENCIA_MAX_BYTES) return MENSAJE_EVIDENCIA_TAMANO;
  return null;
}

// Validación completa: tipo, tamaño y cabecera. Devuelve el tipo real o el mensaje de error.
export async function revisarEvidencia(archivo: File): Promise<{ tipo: TipoEvidencia } | { error: string }> {
  const e = validarEvidencia(archivo);
  if (e) return { error: e };
  const real = tipoPorCabecera(await leerCabecera(archivo));
  if (!real || real !== tipoDeclarado(archivo)) return { error: MENSAJE_EVIDENCIA_CABECERA };
  return { tipo: real };
}

// Nombre seguro para la ruta: sin tildes, espacios ni barras; la extensión sale del tipo real.
export function nombreSeguro(nombre: string, tipo: TipoEvidencia): string {
  const ext = tipo === 'application/pdf' ? 'pdf' : tipo === 'image/png' ? 'png' : tipo === 'image/webp' ? 'webp' : 'jpg';
  const base = (nombre.split(/[\\/]/).pop() ?? '').replace(/\.[^.]*$/, '').normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9_-]+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '').slice(0, 60) || 'evidencia';
  return `${base}.${ext}`;
}

export const rutaEvidencia = (companyId: string, planId: string, accionId: string, archivo: string, marca = Date.now()) =>
  `${companyId}/planes/${planId}/acciones/${accionId}/${marca}-${archivo}`;

// ¿La acción tiene evidencia? Las filas de M37 o el evidence_path de M33.
export const tieneEvidencia = (a: Pick<FilaAccion, 'id' | 'evidence_path'>, conEvidencia: Set<string>) =>
  conEvidencia.has(a.id) || !!a.evidence_path?.trim();

export const accionesConEvidencia = (evidencias: Pick<FilaEvidencia, 'action_id'>[]) => new Set(evidencias.map(e => e.action_id));

export async function cargarEvidencias(planId: string): Promise<FilaEvidencia[]> {
  const { data, error } = await supabase.from('emergency_plan_action_evidence').select('*').eq('plan_id', planId).order('created_at');
  if (error) throw new Error(mensajeErrorPE(error, 'leer las evidencias del plan de acción'));
  return (data ?? []).map((r: any) => ({ ...r, size_bytes: Number(r.size_bytes) })) as FilaEvidencia[];
}

// Sube el archivo y registra la fila. Si la fila falla, se quita el archivo subido.
export async function subirEvidencia(
  accion: Pick<FilaAccion, 'id' | 'company_id' | 'plan_id'>, archivo: File, usuarioId: string | null,
): Promise<FilaEvidencia> {
  const r = await revisarEvidencia(archivo);
  if ('error' in r) throw new Error(r.error);
  const nombre = nombreSeguro(archivo.name, r.tipo);
  const ruta = rutaEvidencia(accion.company_id, accion.plan_id, accion.id, nombre);
  // Con un File, storage-js usa el tipo del navegador: se envuelve con el tipo real (como sdsParaSubir).
  const cuerpo = archivo.type === r.tipo ? archivo : new File([archivo], nombre, { type: r.tipo });
  const { error: eSubir } = await supabase.storage.from(BUCKET_PLAN).upload(ruta, cuerpo, { contentType: r.tipo, upsert: false });
  if (eSubir) {
    const sinPermiso = /row-level security|unauthorized|403/i.test(eSubir.message ?? '');
    throw new Error(sinPermiso ? MENSAJE_SIN_PERMISO : `No se pudo subir la evidencia: ${eSubir.message}.`);
  }
  const { data, error } = await supabase.from('emergency_plan_action_evidence').insert({
    company_id: accion.company_id, plan_id: accion.plan_id, action_id: accion.id, storage_path: ruta,
    file_name: archivo.name.slice(0, 200) || nombre, mime_type: r.tipo, size_bytes: archivo.size, uploaded_by: usuarioId,
  }).select('*');
  if (error || !data?.length) {
    await supabase.storage.from(BUCKET_PLAN).remove([ruta]).catch(() => {});
    throw new Error(error ? mensajeErrorPE(error, 'registrar la evidencia') : MENSAJE_SIN_PERMISO);
  }
  return { ...(data[0] as FilaEvidencia), size_bytes: Number((data[0] as FilaEvidencia).size_bytes) };
}

// Solo el administrador (RLS). Primero la fila (si la RLS la frena, el archivo queda intacto), después el archivo.
export async function borrarEvidencia(e: Pick<FilaEvidencia, 'id' | 'storage_path'>): Promise<void> {
  const { data, error } = await supabase.from('emergency_plan_action_evidence').delete().eq('id', e.id).select('id');
  if (error) throw new Error(mensajeErrorPE(error, 'borrar la evidencia'));
  if (!data?.length) throw new Error(MENSAJE_SIN_PERMISO);
  const { error: eArchivo } = await supabase.storage.from(BUCKET_PLAN).remove([e.storage_path]);
  if (eArchivo) console.error('borrarEvidencia: archivo', eArchivo);
}

export async function urlEvidencia(e: Pick<FilaEvidencia, 'storage_path'>): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_PLAN).createSignedUrl(e.storage_path, SEGUNDOS_URL_EVIDENCIA);
  if (error || !data?.signedUrl) throw new Error(`No se pudo abrir la evidencia: ${error?.message ?? 'sin enlace'}.`);
  return data.signedUrl;
}

export const tamanoLegible = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
